import { getCurrencySymbol, useHandleBillingError } from '@/features/billing';
import { CollapseTransition, Icon } from '@/features/common';
import { useWorkspaceId } from '@/hooks/router';
import {
  getCustomerQueryKey,
  getInvoiceSummaryQueryKey,
} from '@/queries/subscriptions/keys';
import {
  useDeleteSubscriptionPromoCodeMutation,
  usePostSubscriptionPromoCodeMutation,
} from '@/queries/subscriptions/mutations';
import { useGetCustomer } from '@/queries/subscriptions/queries';
import { queryClient } from '@/utils/reactQuery';
import styled from '@emotion/styled';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { DSButton, DSField, DSInput } from '@hundred5/design-system';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect, useState } from 'react';
import { object, string } from 'yup';

type TFormValues = {
  code: string;
};

const validationSchema = object().shape({
  code: string().required('Please enter code'),
});

export function BillingCouponForm() {
  const workspaceId = useWorkspaceId();
  const { data: customer } = useGetCustomer(Number(workspaceId));
  const addPromoCodeMutation = usePostSubscriptionPromoCodeMutation();
  const deletePromoCodeMutation = useDeleteSubscriptionPromoCodeMutation();
  const { handleUnknownError } = useHandleBillingError();
  const [expanded, setExpanded] = useState<boolean>(false);

  const promotionCode = customer?.discount?.promotionCode.code;
  const percentOff =
    !!customer?.discount?.coupon.percentOff &&
    `${customer.discount.coupon.percentOff}%`;
  const amountOff =
    !!customer?.discount?.coupon.amountOff &&
    `${getCurrencySymbol(customer.currency)}${(customer.discount.coupon.amountOff / 100).toFixed(2)}`;
  const durationInMonths =
    !!customer?.discount?.coupon.durationInMonths &&
    `for ${customer.discount.coupon.durationInMonths} months`;
  const appliedPromotion =
    promotionCode &&
    `${percentOff || amountOff} off ${durationInMonths || customer.discount.coupon.duration}`;

  useEffect(() => {
    if (promotionCode) {
      setExpanded(true);
    }
  }, [promotionCode]);

  const handleAddCoupon = (
    values: TFormValues,
    helpers: FormikHelpers<TFormValues>
  ) => {
    addPromoCodeMutation.mutate(
      {
        workspace_id: Number(workspaceId),
        data: {
          promoCode: values.code,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getCustomerQueryKey(Number(workspaceId)),
          });
          queryClient.invalidateQueries({
            queryKey: getInvoiceSummaryQueryKey(Number(workspaceId), {}),
          });
        },
        onError: () => {
          helpers.setFieldError('code', 'Invalid code');
        },
      }
    );
  };

  const handleRemoveCoupon = () => {
    deletePromoCodeMutation.mutate(
      { workspace_id: Number(workspaceId) },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getCustomerQueryKey(Number(workspaceId)),
          });
          queryClient.invalidateQueries({
            queryKey: getInvoiceSummaryQueryKey(Number(workspaceId), {}),
          });
        },
        onError: (error) => {
          handleUnknownError('Failed to remove promo code', error);
        },
      }
    );
  };

  return (
    <div>
      <Header
        expanded={expanded}
        onClick={() => setExpanded((prevState) => !prevState)}
      >
        Got a promotion code?
        <Icon icon={regular('angle-up')} fontSize="10px" />
      </Header>

      <CollapseTransition in={expanded}>
        <Formik<TFormValues>
          initialValues={{ code: promotionCode || '' }}
          validationSchema={validationSchema}
          onSubmit={handleAddCoupon}
          enableReinitialize
          isInitialValid={!!promotionCode}
          validateOnBlur={false}
        >
          {({ isValid, initialValues }) => (
            <FormContent>
              <Field name="code">
                {({ field, meta }) => {
                  const error =
                    !!field.value && !!meta.error ? meta.error : null;

                  return (
                    <DSField for="code" error={error} errorAbsolute>
                      <DSInput
                        id="code"
                        {...field}
                        placeholder="Enter code"
                        error={!!error}
                        disabled={!!initialValues.code}
                      />

                      <Success className={appliedPromotion ? 'show' : ''}>
                        Promotion applied: {appliedPromotion}
                      </Success>
                    </DSField>
                  );
                }}
              </Field>

              {!initialValues.code ? (
                <DSButton
                  type="submit"
                  variant="secondary"
                  disabled={!isValid || addPromoCodeMutation.isPending}
                >
                  Apply
                </DSButton>
              ) : (
                <DSButton
                  variant="secondary"
                  disabled={deletePromoCodeMutation.isPending}
                  onClick={handleRemoveCoupon}
                >
                  Remove
                </DSButton>
              )}
            </FormContent>
          )}
        </Formik>
      </CollapseTransition>
    </div>
  );
}

const Header = styled.div<{ expanded: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  width: max-content;
  cursor: pointer;
  font-size: 12px;
  color: ${(props) => props.theme.typography.colorPrimary};

  svg {
    transform: ${(props) => !props.expanded && 'rotate(180deg)'};
  }
`;

const FormContent = styled(Form)`
  padding-top: 8px;
  display: grid;
  grid-template-columns: minmax(auto, 250px) max-content;
  align-items: flex-start;
  gap: 8px;
`;

const Success = styled.div`
  position: absolute;
  top: 100%;
  font-weight: 400;
  font-size: 12px;
  color: ${(props) => props.theme.colors.green[100]};
  margin-top: 0px;
  max-height: 0px;
  overflow: hidden;
  transition:
    max-height 0.1s,
    margin-top 0.1s;

  &.show {
    max-height: 40px;
    margin-top: 4px;
  }
`;
